import { Extension, type Command } from '@tiptap/core'
import { Plugin } from '@tiptap/pm/state'
import StarterKit from '@tiptap/starter-kit'
import { BulletList, OrderedList } from '@tiptap/extension-list'
import TaskList from '@tiptap/extension-task-list'
import TaskItem from '@tiptap/extension-task-item'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import { Color, FontFamily, FontSize, TextStyle } from '@tiptap/extension-text-style'
import Highlight from '@tiptap/extension-highlight'
import TextAlign from '@tiptap/extension-text-align'

/**
 * 编辑器 schema 的唯一来源。
 *
 * 客户端（src/FocusEditor.tsx）与服务端导出/版本预览（server/editorSchema.ts）必须共用
 * 同一份定义：服务端只用 schema 序列化，一旦少了某个 mark 或属性，导出的 HTML 和版本
 * 预览会**静默**丢掉对应格式。tests/editorSchema.test.ts 守住这条约束。
 */

/** 标题级别，对齐 Google Docs 的「标题 1-6」 */
export const HEADING_LEVELS: Array<1 | 2 | 3 | 4 | 5 | 6> = [1, 2, 3, 4, 5, 6]

/** 默认排版（与 Google Docs 的初始状态一致） */
export const DEFAULT_FONT_FAMILY = 'Arial'
export const DEFAULT_FONT_SIZE_PT = 11

/** 对齐、行距这类块级属性作用在这两种节点上 */
export const BLOCK_TYPES = ['paragraph', 'heading']

/** 列表样式变体：Google Docs 的项目符号 / 编号下拉 */
export const BULLET_LIST_STYLES = [
  { value: 'disc', label: '●  圆点' },
  { value: 'circle', label: '○  空心圆' },
  { value: 'square', label: '■  方块' },
] as const

export const ORDERED_LIST_STYLES = [
  { value: 'decimal', label: '1.  阿拉伯数字' },
  { value: 'lower-alpha', label: 'a.  小写字母' },
  { value: 'upper-alpha', label: 'A.  大写字母' },
  { value: 'lower-roman', label: 'i.  小写罗马数字' },
  { value: 'upper-roman', label: 'I.  大写罗马数字' },
] as const

const listStyleAttribute = {
  listStyle: {
    default: null,
    parseHTML: (element: HTMLElement) => element.getAttribute('data-list-style'),
    renderHTML: (attributes: Record<string, unknown>) =>
      attributes.listStyle ? { 'data-list-style': String(attributes.listStyle) } : {},
  },
}

export const StyledBulletList = BulletList.extend({
  addAttributes() {
    return { ...this.parent?.(), ...listStyleAttribute }
  },
})

export const StyledOrderedList = OrderedList.extend({
  addAttributes() {
    return { ...this.parent?.(), ...listStyleAttribute }
  },
})

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    focusListStyle: {
      /** 给光标所在的列表设置样式变体 */
      setFocusListStyle: (listStyle: string | null) => ReturnType
    }
    focusLineHeight: {
      /** 设置光标所在段落的行距（块级属性，作用于整段） */
      setFocusLineHeight: (lineHeight: string) => ReturnType
      unsetFocusLineHeight: () => ReturnType
    }
  }
}

/**
 * 行距。
 *
 * 本来想直接用 `@tiptap/extension-text-style` 的 LineHeight，但那个扩展自相矛盾：
 * `addGlobalAttributes()` 按 `types` 把属性注册到段落/标题节点上，命令却写死成
 * `chain().setMark('textStyle', { lineHeight })`。配成块级属性后，命令会往一个
 * 并不存在的文字标记属性上写，结果是**静默不生效**（测试里就是这么抓到的）。
 * 所以这里自己实现一份块级的。
 */
const LineHeightCommands = Extension.create({
  name: 'focusLineHeight',
  addOptions() {
    return { types: [...BLOCK_TYPES] as string[] }
  },
  addGlobalAttributes() {
    return [{
      types: this.options.types,
      attributes: {
        lineHeight: {
          default: null,
          parseHTML: (element: HTMLElement) => element.style.lineHeight || null,
          renderHTML: (attributes: Record<string, unknown>) =>
            attributes.lineHeight ? { style: `line-height: ${String(attributes.lineHeight)}` } : {},
        },
      },
    }]
  },
  addCommands() {
    const apply = (lineHeight: string | null): Command =>
      ({ state, dispatch }) => {
        const { $from } = state.selection
        for (let depth = $from.depth; depth > 0; depth -= 1) {
          const node = $from.node(depth)
          if (node.type.name !== 'paragraph' && node.type.name !== 'heading') continue
          if (dispatch) {
            dispatch(
              state.tr.setNodeMarkup($from.before(depth), undefined, { ...node.attrs, lineHeight }),
            )
          }
          return true
        }
        return false
      }

    return {
      setFocusLineHeight: (lineHeight: string) => apply(lineHeight),
      unsetFocusLineHeight: () => apply(null),
    }
  },
})

const ListStyleCommands = Extension.create({
  name: 'focusListStyle',
  addCommands() {
    return {
      setFocusListStyle:
        (listStyle) =>
          ({ state, dispatch }) => {
            const { $from } = state.selection
            for (let depth = $from.depth; depth > 0; depth -= 1) {
              const node = $from.node(depth)
              if (node.type.name !== 'bulletList' && node.type.name !== 'orderedList') continue
              if (dispatch) {
                dispatch(
                  state.tr.setNodeMarkup($from.before(depth), undefined, {
                    ...node.attrs,
                    listStyle,
                  }),
                )
              }
              return true
            }
            return false
          },
    }
  },
})

export const FocusTaskItem = TaskItem.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      taskId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-task-id'),
        renderHTML: (attributes) =>
          attributes.taskId ? { 'data-task-id': attributes.taskId } : {},
      },
      dueAt: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-due-at'),
        renderHTML: () => ({}),
      },
    }
  },
  /**
   * Tiptap v3 的 TaskItem 带 `addNodeView()`，它手工搭 DOM，**完全绕过 renderHTML**，
   * 而 NodeView 只拿到「属性渲染结果」（data-checked / data-task-id），
   * 所以 `renderHTML` 里那个 `data-type="taskItem"` **在真实 DOM 里不存在**
   * （只有 getHTML() 序列化出来才有）。两个后果：
   *   1. 样式里 `li[data-type="taskItem"]` 永远匹配不到 → checkbox 与文字上下堆叠；
   *   2. 复制待办项时剪贴板拿的是真实 DOM → 粘贴回来退化成「空待办项 + 普通列表项」。
   * styles.css 已改用真实存在的 `li[data-checked]`；这里再补一条解析规则，
   * 让剪贴板/导出的 HTML 即使没有 data-type 也能解析回待办项。
   */
  parseHTML() {
    return [
      ...(this.parent?.() ?? []),
      // priority 必须高于 listItem 的 `li` 规则（隐式 50），否则会被普通列表项抢走
      { tag: 'li[data-checked]', priority: 51 },
    ]
  },
})

/** 待办项没有 id 时自动补一个，方便后续按 id 引用某一条 */
const EnsureTaskIds = Extension.create({
  name: 'ensureTaskIds',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        appendTransaction: (_transactions, _oldState, newState) => {
          const transaction = newState.tr
          let changed = false

          newState.doc.descendants((node, position) => {
            if (node.type.name === 'taskItem' && !node.attrs.taskId) {
              transaction.setNodeMarkup(position, undefined, {
                ...node.attrs,
                taskId: crypto.randomUUID(),
              })
              changed = true
            }
          })

          return changed ? transaction : null
        },
      }),
    ]
  },
})

export const sharedEditorExtensions = [
  StarterKit.configure({
    undoRedo: false,
    link: false,
    // 换成下面带列表样式变体的版本
    bulletList: false,
    orderedList: false,
    heading: { levels: HEADING_LEVELS },
  }),
  StyledBulletList,
  StyledOrderedList,
  ListStyleCommands,
  LineHeightCommands,
  TaskList,
  FocusTaskItem.configure({ nested: true }),
  Image.configure({ allowBase64: false, inline: false }),
  Link.configure({ openOnClick: true, autolink: true, defaultProtocol: 'https' }),
  TextStyle,
  Color,
  FontFamily,
  FontSize,
  Highlight.configure({ multicolor: true }),
  TextAlign.configure({
    types: BLOCK_TYPES,
    alignments: ['left', 'center', 'right', 'justify'],
  }),
  EnsureTaskIds,
]

/** 客户端沿用的旧名字 */
export const baseEditorExtensions = sharedEditorExtensions
